import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { search } from '../services/api';
import api from '../services/api';
import KtruDashboard from '../components/KtruDashboard';
import KtruExtended from '../components/KtruExtended';
import ktruMap from '../assets/ktru_map.json';

const LOADING_PHRASES = [
    'Поиск по базе данных',
    'Провожу анализ',
    'Обрабатываю результаты',
    'Создаю сводный отчёт',
    'Сортирую данные',
    'Почти готово',
];

const SearchLoader = () => {
    const [idx, setIdx] = useState(0);
    const [fade, setFade] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setFade(false);
            setTimeout(() => {
                setIdx(i => (i + 1) % LOADING_PHRASES.length);
                setFade(true);
            }, 300);
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="search-loader">
            <div className="search-loader-dots">
                <span /><span /><span />
            </div>
            <div className={`search-loader-text ${fade ? 'visible' : ''}`}>
                {LOADING_PHRASES[idx]}
            </div>
        </div>
    );
};

const formatBigNumber = (n) => {
    if (!n) return '—';
    return new Intl.NumberFormat('ru-RU').format(n);
};

// Форматирует ввод в маску КТРУ: DD.DD.DD.DDD-DDDDDDDD
const formatKtruInput = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 17);
    let result = '';
    for (let i = 0; i < digits.length; i++) {
        if (i === 2 || i === 4 || i === 6) result += '.';
        if (i === 9) result += '-';
        result += digits[i];
    }
    return result;
};

// Форматирует ввод в маску ОКПД: DD.DD.DD.DDD
const formatOkpdInput = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 9);
    let result = '';
    for (let i = 0; i < digits.length; i++) {
        if (i === 2 || i === 4 || i === 6) result += '.';
        result += digits[i];
    }
    return result;
};

const MainPage = ({ user }) => {

    const [searchParams] = useSearchParams();

    const [searchMode, setSearchMode] = useState('ktru'); // 'ktru' | 'okpd'
    const [query, setQuery] = useState('');
    const [ktruView, setKtruView] = useState('dashboard');
    const [activeKtru, setActiveKtru] = useState('');

    const [results, setResults] = useState([]);
    const [okpdResults, setOkpdResults] = useState([]);

    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const stats = {
        contracts: 662832,
        products: 1237744,
        ktru_codes: 18959,
        suppliers: 44815,
    };

    const [currentPage, setCurrentPage] = useState(1);

    const DEFAULT_KTRU = '26.60.12.132-00000036';

    useEffect(() => {
        const mode = searchParams.get('mode');
        const q = searchParams.get('q');

        if (mode === 'okpd' && q) {
            setSearchMode('okpd');
            setQuery(q);
            setHasSearched(true);
            setLoading(true);
            api.get(`/okpd/${q}`)
                .then(r => setOkpdResults(r.data || []))
                .catch(() => setOkpdResults([]))
                .finally(() => setLoading(false));
            return;
        }

        // Загружаем дефолтный КТРУ из статического файла — мгновенно, без бекенда
        fetch('/default_ktru.json')
            .then(r => r.json())
            .then(data => {
                setResults(data);
                setActiveKtru(DEFAULT_KTRU);
                setQuery(formatKtruInput(DEFAULT_KTRU));
                setHasSearched(true);
            })
            .catch(() => {}); // если файл недоступен — просто пустая страница
    }, []);

    const handleSearch = async () => {
        if (!query.trim()) return;

        try { window.ym(109101254, 'reachGoal', 'find'); } catch {}

        setLoading(true);
        setHasSearched(true);
        setCurrentPage(1);

        if (searchMode === 'okpd') {
            try {
                const response = await api.get(`/okpd/${query.trim()}`);
                setOkpdResults(response.data || []);
                setResults([]);
            } catch (error) {
                console.error('Ошибка поиска ОКПД:', error);
                setOkpdResults([]);
            } finally {
                setLoading(false);
            }
            return;
        }

        setKtruView('dashboard');
        try {
            const response = await search.getKtruDashboard(query);
            setResults(response.data || []);
            setActiveKtru(query.trim());
        } catch (error) {
            console.error('Ошибка поиска:', error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKtruFromOkpd = async (ktru) => {
        setSearchMode('ktru');
        setQuery(ktru);
        setHasSearched(true);
        setKtruView('dashboard');
        setLoading(true);
        setOkpdResults([]);
        try {
            const response = await search.getKtruDashboard(ktru);
            setResults(response.data || []);
            setActiveKtru(ktru);
        } catch (error) {
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const formatPrice = (price) => {
        if (!price) return 'Цена не указана';

        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'RUB',
            maximumFractionDigits: 0
        }).format(price);
    };

    const filteredResults = results;

    return (
        <div>

            <div className="container">

                {/* SEARCH */}
                <div className="search-card">
                    <div className="search-outer">

                        {/* Левая часть — КТРУ код + расшифровка */}
                        {activeKtru && (
                            <div className="search-ktru-info">
                                <div className="search-ktru-label">КТРУ</div>
                                <a
                                    className="search-ktru-code"
                                    href={`https://zakupki.gov.ru/epz/ktru/search/results.html?searchString=${activeKtru}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {activeKtru}
                                </a>
                                {ktruMap[activeKtru] && (
                                    <div className="search-ktru-name">{ktruMap[activeKtru]}</div>
                                )}
                            </div>
                        )}

                        {/* Середина — инпут */}
                        <div className="search-middle">
                            <div className="search-input-row">
                                <div className="search-input-wrap">
                                    <div className="search-input-mask-wrap">
                                        <input
                                            type="text"
                                            className="search-input search-input-masked"
                                            value={query}
                                            onChange={(e) => setQuery(
                                                searchMode === 'ktru'
                                                    ? formatKtruInput(e.target.value)
                                                    : formatOkpdInput(e.target.value)
                                            )}
                                            onKeyPress={handleKeyPress}
                                            maxLength={searchMode === 'ktru' ? 21 : 12}
                                        />
                                        <div className="search-input-ghost" aria-hidden="true">
                                            <span className="ghost-typed">{query}</span>
                                            <span className="ghost-remain">
                                                {(searchMode === 'ktru' ? '00.00.00.000-00000000' : '00.00.00.000').slice(query.length)}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        className="search-clear-btn"
                                        onClick={() => {
                                            setQuery('');
                                            setResults([]);
                                            setHasSearched(false);
                                            setActiveKtru('');
                                        }}
                                        title="Очистить"
                                    >
                                        ✕
                                    </button>
                                </div>
                                <button
                                    className="search-go-btn"
                                    onClick={handleSearch}
                                    disabled={loading}
                                    title="Найти"
                                >
                                    {loading
                                        ? <span className="search-spinner" />
                                        : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                    }
                                </button>
                            </div>
                        </div>

                        {/* Правая часть */}
                        <div className="search-right-actions">
                            <div className="search-type-toggle">
                                <button
                                    className={`toggle-btn${searchMode === 'ktru' ? ' active' : ''}`}
                                    onClick={() => { setSearchMode('ktru'); setQuery(''); setResults([]); setOkpdResults([]); setHasSearched(false); }}
                                >
                                    По КТРУ
                                </button>
                                <button
                                    className={`toggle-btn${searchMode === 'okpd' ? ' active' : ''}`}
                                    onClick={() => { setSearchMode('okpd'); setQuery(''); setResults([]); setOkpdResults([]); setHasSearched(false); }}
                                >
                                    По ОКПД
                                </button>
                            </div>
                            {searchMode === 'ktru' && (
                                <button
                                    className="search-action-btn"
                                    onClick={() => setKtruView(v => v === 'dashboard' ? 'extended' : 'dashboard')}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="10" y2="18"/></svg>
                                    {ktruView === 'dashboard' ? 'Расширенный поиск' : 'Дашборд'}
                                </button>
                            )}
                        </div>

                    </div>
                </div>

                {/* TECH MODE BANNER + STATS — показываем только до первого поиска */}
                {!hasSearched && (
                    <>
                        <div className="tech-banner">
                            <span className="tech-banner-icon">🔧</span>
                            <div className="tech-banner-text">
                                <strong>Технический режим</strong> — сервис находится в стадии тестирования. Возможны неточности в данных и перебои в работе. Мы активно работаем над улучшением.
                            </div>
                        </div>

                        {stats && (
                            <div className="home-stats">
                                <div className="home-stat-card">
                                    <div className="home-stat-icon">📄</div>
                                    <div className="home-stat-value">{formatBigNumber(stats.contracts)}</div>
                                    <div className="home-stat-label">контрактов проанализировано</div>
                                </div>
                                <div className="home-stat-card">
                                    <div className="home-stat-icon">📦</div>
                                    <div className="home-stat-value">{formatBigNumber(stats.products)}</div>
                                    <div className="home-stat-label">позиций в базе</div>
                                </div>
                                <div className="home-stat-card">
                                    <div className="home-stat-icon">🏷️</div>
                                    <div className="home-stat-value">{formatBigNumber(stats.ktru_codes)}</div>
                                    <div className="home-stat-label">уникальных кодов КТРУ</div>
                                </div>
                                <div className="home-stat-card">
                                    <div className="home-stat-icon">🏢</div>
                                    <div className="home-stat-value">{formatBigNumber(stats.suppliers)}</div>
                                    <div className="home-stat-label">поставщиков в базе</div>
                                </div>
                            </div>
                        )}
                    </>
                )}



{/* RESULTS */}
                <div className="results">

                    {loading && <SearchLoader />}

                    {!loading && hasSearched && searchMode === 'ktru' && filteredResults.length === 0 && (
                        <div className="empty">
                            <div className="empty-icon">🔍</div>
                            <p>Ничего не найдено</p>
                        </div>
                    )}
                    {!loading && hasSearched && searchMode === 'okpd' && okpdResults.length === 0 && (
                        <div className="empty">
                            <div className="empty-icon">🔍</div>
                            <p>Ничего не найдено</p>
                        </div>
                    )}

                    {/* OKPD: список КТРУ кодов */}
                    {!loading && searchMode === 'okpd' && hasSearched && (
                        okpdResults.length === 0 ? null : (
                            <div className="okpd-results">
                                <div className="okpd-results-title">
                                    Коды КТРУ для ОКПД <strong>{query}</strong>
                                </div>
                                {okpdResults.map(item => (
                                    <button
                                        key={item.ktru}
                                        className="okpd-ktru-row"
                                        onClick={() => handleKtruFromOkpd(item.ktru)}
                                    >
                                        <span className="okpd-ktru-code">{item.ktru}</span>
                                        <span className="okpd-ktru-name">{ktruMap[item.ktru] || ''}</span>
                                        <span className="okpd-ktru-count">{item.contracts} контр.</span>
                                    </button>
                                ))}
                            </div>
                        )
                    )}

                    {/* KTRU: дашборд по производителям */}
                    {!loading && searchMode === 'ktru' && filteredResults.length > 0 && ktruView === 'dashboard' && (
                        <KtruDashboard results={filteredResults} ktru={query} />
                    )}

                    {/* KTRU: расширенный список контрактов */}
                    {!loading && searchMode === 'ktru' && filteredResults.length > 0 && ktruView === 'extended' && (
                        <KtruExtended results={filteredResults} ktru={query} />
                    )}

                </div>

            </div>
        </div>
    );
};

export default MainPage;
